// crewbit-admin/src/components/money/DisputeDetailContent.tsx
//
// Tier 2-UI Phase E (in-app bundle) — admin parity for mobile
// DisputeDetailContent. On admin (desktop), the CTA opens Stripe Dashboard
// in a new tab via standard <a target="_blank"> per §E-inapp.D2 — operator
// handles evidence submission natively in Stripe Dashboard. Mobile uses
// clipboard-copy because phone-browser context can't reliably deep-link.

import { ExternalLink, Calendar, FileText, User } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import type { DisputeEscalation, DisputeStatus } from '../../types/disputes';
import DisputeStatusPill from './DisputeStatusPill';

interface DisputeDetailContentProps {
  dispute: DisputeEscalation;
  statusLabels: Record<DisputeStatus, string>;
  copy: {
    stripeSays: (value: string) => string;
    invoiceLabel: string;
    invoiceMissing: string;
    clientLabel: string;
    clientUnknown: string;
    invoiceTotalLabel: string;
    reasonLabel: string;
    openedAtLabel: string;
    evidenceSubmittedLabel: string;
    resolvedAtLabel: string;
    resolutionAmountLabel: string;
    openInStripe: string;
    openInStripeHelper: string;
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function DisputeDetailContent({
  dispute,
  statusLabels,
  copy,
}: DisputeDetailContentProps) {
  const stripeUrl = `https://dashboard.stripe.com/disputes/${dispute.stripe_dispute_id}`;
  const amountDollars = dispute.amount_cents != null ? dispute.amount_cents / 100 : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <DisputeStatusPill status={dispute.status} label={statusLabels[dispute.status]} />
        </div>
        {dispute.stripe_status ? (
          <div
            style={{
              fontSize: typography.size.caption,
              color: palette.g500,
              fontStyle: 'italic',
              marginTop: spacing.xs,
            }}
          >
            {copy.stripeSays(dispute.stripe_status)}
          </div>
        ) : null}
        {amountDollars != null ? (
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: palette.g800,
              fontVariantNumeric: 'tabular-nums',
              marginTop: spacing.sm,
            }}
          >
            {formatMoney(amountDollars)}
          </div>
        ) : null}
      </Card>

      <Card>
        <DetailRow
          icon={<FileText size={16} color={palette.g500} strokeWidth={2} />}
          label={copy.invoiceLabel}
          value={dispute.invoice_number ? `#${dispute.invoice_number}` : copy.invoiceMissing}
        />
        <DetailRow
          icon={<User size={16} color={palette.g500} strokeWidth={2} />}
          label={copy.clientLabel}
          value={dispute.client_name ?? copy.clientUnknown}
        />
        {dispute.invoice_total != null ? (
          <DetailRow label={copy.invoiceTotalLabel} value={formatMoney(dispute.invoice_total)} />
        ) : null}
        {dispute.reason ? (
          <DetailRow label={copy.reasonLabel} value={dispute.reason} multiline />
        ) : null}
      </Card>

      <Card>
        <DetailRow
          icon={<Calendar size={16} color={palette.g500} strokeWidth={2} />}
          label={copy.openedAtLabel}
          value={formatDate(dispute.created_at)}
        />
        {dispute.evidence_submitted_at ? (
          <DetailRow
            label={copy.evidenceSubmittedLabel}
            value={formatDate(dispute.evidence_submitted_at)}
          />
        ) : null}
        {dispute.resolution_at ? (
          <DetailRow
            label={copy.resolvedAtLabel}
            value={formatDate(dispute.resolution_at)}
          />
        ) : null}
        {dispute.resolution_amount != null ? (
          <DetailRow
            label={copy.resolutionAmountLabel}
            value={formatMoney(dispute.resolution_amount)}
          />
        ) : null}
      </Card>

      <a
        href={stripeUrl}
        target="_blank"
        rel="noreferrer noopener"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: spacing.sm,
          background: palette.accentWash,
          color: palette.accent,
          borderRadius: radii.md,
          padding: spacing.md,
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: typography.size.subheading,
        }}
      >
        <ExternalLink size={16} color={palette.accent} strokeWidth={2} />
        {copy.openInStripe}
      </a>
      <div
        style={{
          fontSize: typography.size.caption,
          color: palette.g500,
          textAlign: 'center',
          fontStyle: 'italic',
        }}
      >
        {copy.openInStripeHelper}
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: palette.white,
        borderRadius: radii.md,
        border: `1px solid ${palette.g150}`,
        padding: spacing.md,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md,
      }}
    >
      {children}
    </div>
  );
}

interface DetailRowProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  multiline?: boolean;
}

function DetailRow({ icon, label, value, multiline }: DetailRowProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {icon}
        <span style={{ fontSize: typography.size.caption, color: palette.g500 }}>{label}</span>
      </div>
      <div
        style={{
          fontSize: typography.size.body,
          color: palette.g800,
          ...(multiline
            ? {}
            : {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }),
        }}
      >
        {value}
      </div>
    </div>
  );
}
